/**
 * Order Reassignment Component
 * Allows Team Leader / Admin to manually reassign orders
 * 
 * Features:
 * - Processor selection dropdown with capacity info
 * - Lock assignment checkbox
 * - Reason for reassignment
 * - Audit trail
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  UserPlus, 
  Lock, 
  Unlock,
  RefreshCw,
  Check,
  X
} from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

const API = process.env.REACT_APP_BACKEND_URL || '';

const OrderReassignmentDialog = ({ 
  isOpen, 
  onClose, 
  order, 
  onSuccess,
  currentUserId,
  currentUserRole 
}) => {
  const [processors, setProcessors] = useState([]);
  const [teamLabel, setTeamLabel] = useState('');
  const [eligibilityRelaxed, setEligibilityRelaxed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedProcessor, setSelectedProcessor] = useState('');
  const [lockAssignment, setLockAssignment] = useState(false);
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (isOpen && order) {
      fetchEligibleProcessors();
      setSelectedProcessor(order.assignee_id || '');
      setLockAssignment(order.assignment_locked || false);
      setReason('');
    }
  }, [isOpen, order]);

  const fetchEligibleProcessors = async () => {
    try {
      setLoading(true);
      // Jul 2026 — REASSIGN-TEAM-FILTER-001: pass order_id so the backend
      // scopes the list exactly like the assignment engine — Search
      // products (AVR, Full Search, O&E, ...) → Search Team processors,
      // Tax products (Tax Search, MLS, ...) → Tax Team processors, plus
      // state / client / product eligibility. Never mixes teams.
      const response = await axios.get(`${API}/api/auto-assignment/eligible-processors`, {
        params: { order_id: order.id }
      });
      setProcessors(response.data.processors || []);
      setTeamLabel(response.data.team_label || '');
      setEligibilityRelaxed(Boolean(response.data.eligibility_relaxed));
    } catch (err) {
      console.error('Error fetching processors:', err);
      toast.error('Failed to load eligible processors');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedProcessor) {
      toast.error('Please select a processor');
      return;
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/auto-assignment/orders/${order.id}/reassign`, {
        new_assignee_id: selectedProcessor,
        assignment_locked: lockAssignment,
        reason: reason || undefined
      }, {
        params: {
          assigned_by_user_id: currentUserId,
          assigned_by_role: currentUserRole
        },
        headers: { Authorization: `Bearer ${token}` }
      });

      const processor = processors.find(p => p.id === selectedProcessor);
      toast.success(`Order reassigned to ${processor?.full_name || 'processor'}`);
      
      if (onSuccess) {
        onSuccess({
          assignee_id: selectedProcessor,
          assignee_name: processor?.full_name,
          assignment_locked: lockAssignment
        });
      }
      onClose();
    } catch (err) {
      console.error('Error reassigning order:', err);
      toast.error(err.response?.data?.detail || 'Failed to reassign order');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnassign = async () => {
    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/auto-assignment/orders/${order.id}/unassign`, null, {
        params: {
          reason: 'Manual unassignment',
          assigned_by_user_id: currentUserId,
          assigned_by_role: currentUserRole
        },
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Order unassigned successfully');
      
      if (onSuccess) {
        onSuccess({
          assignee_id: null,
          assignee_name: null,
          assignment_locked: false
        });
      }
      onClose();
    } catch (err) {
      console.error('Error unassigning order:', err);
      toast.error(err.response?.data?.detail || 'Failed to unassign order');
    } finally {
      setSubmitting(false);
    }
  };

  if (!order) return null;

  const terminalStatus = ['complete', 'completed', 'cancelled'].includes((order.status || '').toLowerCase());
  if (terminalStatus) {
    const word = (order.status || '').toLowerCase() === 'cancelled' ? 'cancelled' : 'completed';
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-md" data-testid="reassign-blocked-dialog">
          <DialogHeader>
            <DialogTitle className="capitalize">{word} order cannot be reassigned</DialogTitle>
            <DialogDescription className="pt-2 text-sm leading-relaxed">
              This order has already been {word}. Reopen the order through the
              authorized reopen process before changing the assignee.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={onClose} data-testid="reassign-blocked-ok-btn">OK</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="reassign-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-blue-600" />
            Reassign Order
          </DialogTitle>
          <DialogDescription>
            Order: {order.order_number}
            {order.state && <span className="ml-2 text-blue-600">({order.state})</span>}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Current Assignment Info */}
          {order.assignee_name && (
            <div className="p-3 bg-gray-50 rounded-lg text-sm">
              <span className="text-gray-500">Currently assigned to:</span>
              <span className="ml-2 font-medium">{order.assignee_name}</span>
              {order.assignment_locked && (
                <Lock className="h-3 w-3 inline-block ml-2 text-orange-500" />
              )}
            </div>
          )}

          {/* Processor Selection */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Assign to Processor</Label>
              {teamLabel && (
                <span
                  className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200"
                  data-testid="reassign-team-badge"
                >
                  {teamLabel}
                </span>
              )}
            </div>
            {eligibilityRelaxed && (
              <p className="text-xs text-amber-600" data-testid="eligibility-relaxed-note">
                No processor matches this order's state/product eligibility — showing all {teamLabel || 'team'} processors.
              </p>
            )}
            {loading ? (
              <div className="flex items-center justify-center py-4">
                <RefreshCw className="h-5 w-5 animate-spin text-gray-400" />
              </div>
            ) : (
              <Select value={selectedProcessor} onValueChange={setSelectedProcessor}>
                <SelectTrigger data-testid="processor-select">
                  <SelectValue placeholder="Select a processor..." />
                </SelectTrigger>
                <SelectContent>
                  {processors.map((proc) => {
                    const atWip = proc.active_load >= proc.wip_limit;
                    const overWip = proc.active_load > proc.wip_limit;
                    return (
                      <SelectItem
                        key={proc.id}
                        value={proc.id}
                        data-testid={`processor-option-${proc.id}`}
                      >
                        <div className="flex items-center justify-between w-full gap-2">
                          <span>{proc.full_name}</span>
                          {/* Jul 2026 — REASSIGN-ELIGIBILITY-002: exact reason labels.
                              ineligible_reason only appears in relaxed mode (nobody on
                              the team matches this order's state/product config), so
                              entries stay SELECTABLE — informational, never a deadlock.
                              In strict mode ineligible processors are hidden entirely. */}
                          {proc.ineligible_reason ? (
                            <span className="text-xs text-red-500 ml-2" data-testid={`ineligible-reason-${proc.id}`}>
                              {proc.ineligible_reason}
                            </span>
                          ) : atWip ? (
                            <span className="text-xs font-medium text-amber-600 ml-2" data-testid={`wip-label-${proc.id}`}>
                              {overWip ? 'Over WIP limit' : 'At WIP limit'} — {proc.active_load}/{proc.wip_limit}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-500 ml-2">
                              ({proc.active_load}/{proc.wip_limit} WIP)
                            </span>
                          )}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Lock Assignment Checkbox */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="lockAssignment"
              checked={lockAssignment}
              onCheckedChange={setLockAssignment}
              data-testid="lock-assignment-checkbox"
            />
            <div className="flex items-center gap-2">
              {lockAssignment ? (
                <Lock className="h-4 w-4 text-orange-500" />
              ) : (
                <Unlock className="h-4 w-4 text-gray-400" />
              )}
              <Label htmlFor="lockAssignment" className="cursor-pointer">
                Lock assignment (prevent auto-reassignment)
              </Label>
            </div>
          </div>

          {/* Reason — Jul 2026 REASSIGN-ELIGIBILITY-002: becomes MANDATORY
              when the selected processor is at/over their WIP limit
              (server enforces the same rule and tags the audit log). */}
          {(() => {
            const sel = processors.find((p) => p.id === selectedProcessor);
            const atWip = !!sel && sel.active_load >= sel.wip_limit;
            const ineligible = !!sel && !!sel.ineligible_reason;
            const needsOverride = atWip || ineligible;
            return (
              <div className="space-y-2">
                {ineligible && (
                  <div className="p-2.5 rounded-md bg-red-50 border border-red-300 text-xs text-red-800" data-testid="eligibility-override-warning">
                    <span className="font-semibold">{sel.full_name}: {sel.ineligible_reason}.</span>{' '}
                    Assigning anyway requires an override reason and will be recorded in the assignment audit log.
                  </div>
                )}
                {atWip && (
                  <div className="p-2.5 rounded-md bg-amber-50 border border-amber-300 text-xs text-amber-800" data-testid="wip-override-warning">
                    <span className="font-semibold">{sel.full_name} is {sel.active_load > sel.wip_limit ? 'over' : 'at'} their WIP limit ({sel.active_load}/{sel.wip_limit}).</span>{' '}
                    An override reason is required and will be recorded in the assignment audit log.
                  </div>
                )}
                <Label htmlFor="reason">
                  {needsOverride ? (
                    <>Override reason <span className="text-red-500">*</span></>
                  ) : (
                    'Reason (optional)'
                  )}
                </Label>
                <Textarea
                  id="reason"
                  placeholder={needsOverride ? 'Required: why are you overriding this assignment restriction?' : 'Enter reason for reassignment...'}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={2}
                  data-testid="reassign-reason"
                  className={needsOverride && !reason.trim() ? 'border-amber-400 focus-visible:ring-amber-400' : ''}
                />
              </div>
            );
          })()}
        </div>

        <DialogFooter className="flex justify-between sm:justify-between">
          <div>
            {order.assignee_id && (
              <Button
                type="button"
                variant="outline"
                onClick={handleUnassign}
                disabled={submitting}
                className="text-red-600 hover:text-red-700"
                data-testid="unassign-btn"
              >
                <X className="h-4 w-4 mr-1" />
                Unassign
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button 
              type="button" 
              onClick={handleSubmit} 
              disabled={submitting || !selectedProcessor || (() => {
                const sel = processors.find((p) => p.id === selectedProcessor);
                return !!sel && (sel.active_load >= sel.wip_limit || !!sel.ineligible_reason) && !reason.trim();
              })()}
              data-testid="confirm-reassign-btn"
            >
              {submitting ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Check className="h-4 w-4 mr-2" />
              )}
              Reassign
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default OrderReassignmentDialog;
